import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, FileText } from "lucide-react";
import { FadeUp, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ClientLogos from "../components/ClientLogos";
import RequestCvModal from "../components/RequestCvModal";
import { clients } from "../data/projects";

const capabilities = [
  ["Brand Identity", "Logo systems, guidelines and customised brand bibles that survive the real world"],
  ["Packaging", "Structural thinking, shelf presence and production-ready artwork"],
  ["Campaign & Editorial", "Illustrated posters, print advertising, social and programmatic"],
  ["Illustration & Motion", "Hand-drawn character with a brutalist spine, in stills and animation"],
];

const glance = [
  ["Years in practice", "10", "Independent studio since 05/2016, working remotely across Europe, Asia and the US"],
  ["Sectors", "08", "Financial services, aviation, mining and resources, humanitarian supply, hospitality, music and culture, fashion and beauty, non-profit"],
  ["Engagement types", "04", "Agency creative lead, embedded in-house designer, contract bid and document design, direct studio commissions"],
  ["Track record", "59+", "Commissions delivered for 16 global institutions and independent brands, from brand systems to hundreds-of-pages submissions"],
];

export default function About() {
  const [cvOpen, setCvOpen] = useState(false);
  useEffect(() => {
    document.title = "About · Haus Ov Harii";
  }, []);

  return (
    <div className="px-6 pt-32 lg:px-12" data-testid="about-page">
      <div className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeUp className="overflow-hidden border border-white/10">
            <img
              src="https://images.squarespace-cdn.com/content/v1/5b55c129710699aed19f100e/1715849487378-LQ5GFXRDLMKOVMA1EJUV/done-27.jpg"
              alt="Weird Weather brand identity and packaging, 2024"
              className="aspect-[4/5] w-full object-cover transition-transform duration-[1.2s] hover:scale-[1.02]"
            />
          </FadeUp>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">
            Selected work · Weird Weather, 2024
          </p>
        </div>

        <div className="lg:col-span-7">
          <p className="eyebrow mb-6">About</p>
          <h1 className="text-4xl font-medium tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["Design, illustration", "& art direction.", "One practice."]} delay={0.1} />
          </h1>
          <FadeUp delay={0.35}>
            <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-[#9EA3B0] sm:text-lg">
              Haus Ov Harii is the independent studio practice of Harriet Jones, a remote-based
              designer, illustrator and art director working across brand identity, packaging,
              campaign and editorial, with the project management and governance to carry
              complex, multi-stakeholder work from brief to delivery.
            </p>
            <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-[#9EA3B0]">
              The work splits naturally into two rooms. Corporate: brand systems, campaigns and
              large-scale documents delivered inside exacting guidelines and formal review, for
              institutions like JP Morgan &amp; Chase, Cathay Pacific, Orica and the UN supply
              chain. Creative: illustration-led identity and image-making with room to misbehave.
              The standard, and the discipline behind it, is the same in both.
            </p>
          </FadeUp>

          <FadeUp delay={0.15} className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="about-contact-cta"
              className="inline-flex items-center gap-2 bg-umber px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#0B0C0E] transition-opacity hover:opacity-80"
            >
              Start a project <ArrowUpRight size={14} />
            </Link>
            <button
              type="button"
              onClick={() => setCvOpen(true)}
              data-testid="about-cv-cta"
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:border-umber hover:text-umber"
            >
              Request CV <FileText size={14} />
            </button>
            <a
              href="/Haus-Ov-Harii-Portfolio-Summary.pdf"
              download="Haus-Ov-Harii-Portfolio-Summary.pdf"
              data-testid="about-pdf-download"
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors hover:border-umber hover:text-umber"
            >
              One-page summary <Download size={14} />
            </a>
          </FadeUp>
        </div>
      </div>
      <RequestCvModal open={cvOpen} onClose={() => setCvOpen(false)} />

      <ClientLogos className="mt-20" label="Clients & partners" testId="about-client-logos" />

      <section className="mt-20" aria-label="At a glance" data-testid="at-a-glance">
        <FadeUp>
          <p className="eyebrow mb-6">At a glance</p>
        </FadeUp>
        <dl className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {glance.map(([k, v, note], i) => (
            <FadeUp key={k} delay={i * 0.07} className="h-full">
              <div className="flex h-full flex-col bg-[#0B0C0E] p-6" data-testid={`glance-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#616775]">{k}</dt>
                <dd className="mt-3 text-2xl font-light tracking-tight text-[#F9F9FB] sm:text-3xl">{v}</dd>
                <dd className="mt-3 text-sm leading-relaxed text-[#9EA3B0]">{note}</dd>
              </div>
            </FadeUp>
          ))}
        </dl>
      </section>

      <Marquee
        items={["Identity", "Packaging", "Illustration", "Campaign", "Motion", "Art Direction"]}
        className="-mx-6 mt-24 lg:-mx-12"
      />

      <div className="grid gap-14 py-20 lg:grid-cols-12">
        <section className="lg:col-span-7" aria-label="Capabilities">
          <FadeUp>
            <p className="eyebrow mb-8">Capabilities</p>
          </FadeUp>
          <div className="border-t border-white/10">
            {capabilities.map(([title, detail], i) => (
              <FadeUp key={title} delay={i * 0.06} className="grid gap-2 border-b border-white/10 py-7 sm:grid-cols-12">
                <span className="font-mono text-xs text-[#616775] sm:col-span-3">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="sm:col-span-9">
                  <h2 className="text-xl font-light tracking-tight">{title}</h2>
                  <p className="mt-1 text-sm text-[#9EA3B0]">{detail}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </section>

        <section className="lg:col-span-5" aria-label="Client roster">
          <FadeUp>
            <p className="eyebrow mb-8">Selected clients</p>
          </FadeUp>
          <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10" data-testid="client-roster">
            {clients.map((c) => (
              <div key={c} className="bg-[#0B0C0E] px-4 py-5 text-sm text-[#9EA3B0] transition-colors hover:text-white">
                {c}
              </div>
            ))}
          </div>
          <FadeUp delay={0.1}>
            <p className="mt-8 border-l-2 border-umber pl-4 text-sm leading-relaxed text-[#9EA3B0]">
              Remote-based, working worldwide with agencies, institutions and independent
              brands across Europe, Asia and the US.
            </p>
          </FadeUp>
        </section>
      </div>
    </div>
  );
}
